import { useCallback } from 'react';

import { useActor } from '@xstate/react';

import { useGlobalAppState } from '../../../../context/GlobalAppState';
import { useCart } from '../../../../hooks';

import type { ReorderEvent, ReorderStep } from '../../machines/Reorder.machine';

// ─────────────────────────────────────────────────────────────────────────────

export const useGroupOrderReorderConfirmation = () => {
  const { reorderMachineRef } = useGlobalAppState();
  const { cart } = useCart();

  const [, sendReorderEvent] = useActor(reorderMachineRef);

  // ─── Derived ───────────────────────────────────────────────────────────────

  const isGroupOrderHost = Boolean(
    cart?.multipartyDetails?.joinCode && cart.currentUserOrderPermissions.canSubmitOrder,
  );

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const requestGroupOrderReorderConfirmation = useCallback(
    (params: RequestParams) => {
      const { reorderEvent } = params;

      const reorderState = reorderMachineRef.getSnapshot();
      const groupOrderReorderRequest = reorderState?.context.groupOrderReorderRequest;

      if (groupOrderReorderRequest) return;

      sendReorderEvent({
        type: 'REQUEST_GROUP_ORDER_REORDER_CONFIRMATION',
        reorderEvent,
        originStep: reorderState?.value as ReorderStep,
      });
    },
    [reorderMachineRef, sendReorderEvent],
  );

  // ───────────────────────────────────────────────────────────────────────────

  return {
    isGroupOrderHost,
    requestGroupOrderReorderConfirmation,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type RequestParams = {
  reorderEvent: ReorderEvent;
};
